<script setup>
import { computed, onMounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import MetricCard from './MetricCard.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { METRIC_CHART } from '../../constants';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import subMonths from 'date-fns/subMonths';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const accountReport = useMapGetter('getAccountReports');
const inboxes = useMapGetter('inboxes/getInboxes');
const currentUser = useMapGetter('getCurrentUser');

const selectedFilter = ref('7days');
const selectedInbox = ref(null);
const [showInboxDropdown, toggleInboxDropdown] = useToggle();

const filterOptions = [
  { label: 'Last 7 Days', value: '7days' },
  { label: 'Last 14 Days', value: '14days' },
  { label: 'Last 30 Days', value: '30days' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: '2nd Last Month', value: 'secondLastMonth' },
];

const safeInboxes = computed(() =>
  Array.isArray(inboxes.value) ? inboxes.value : []
);

const inboxMenuItems = computed(() => [
  {
    label: t('INBOX_REPORTS.ALL_INBOXES'),
    value: null,
    action: 'select_inbox',
  },
  ...safeInboxes.value.map(inbox => ({
    label: inbox?.name || '',
    value: inbox?.id ?? '',
    action: 'select_inbox',
  })),
]);

const selectedInboxFilter = computed(() => {
  if (!selectedInbox.value) {
    return { label: t('INBOX_REPORTS.ALL_INBOXES') };
  }
  return inboxMenuItems.value.find(
    item => item.value === selectedInbox.value.id
  );
});

const isLoading = computed(
  () => accountReport.value.isFetching.outgoing_messages_count
);

const formatYAxisTick = value =>
  new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value || 0);

const chartOptions = computed(() => {
  const baseOptions = METRIC_CHART.outgoing_messages_count || {};
  const baseScales = baseOptions.scales || {};
  const baseY = baseScales.y || {};
  const baseYTicks = baseY.ticks || {};

  return {
    ...baseOptions,
    scales: {
      ...baseScales,
      x: {
        type: 'category',
        grid: {
          drawOnChartArea: false,
          display: false,
        },
        border: {
          display: true,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: 10,
          font: {
            size: 11,
          },
          padding: 8, // Space between labels and chart
        },
      },
      y: {
        ...baseY,
        type: 'linear',
        position: 'left',
        min: 0,
        border: {
          display: true,
        },
        ticks: {
          ...baseYTicks,
          stepSize: 1,
          precision: 0,
          maxTicksLimit: 6,
          padding: 8, // Space between labels and chart
          callback: value => {
            if (value < 0 || value % 1 !== 0) return null;
            return formatYAxisTick(value);
          },
        },
        grid: {
          drawOnChartArea: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        afterDataLimits: scale => {
          scale.min = 0;
          if (scale.max <= 0) {
            scale.max = 10;
          }
        },
      },
    },
    layout: {
      padding: {
        left: 5,
        right: 5,
        top: 10,
        bottom: 25, // Extra space for rotated x-axis labels
      },
    },
  };
});

const buildDateSeries = () => {
  const data = accountReport.value.data.outgoing_messages_count || [];
  const { from, to } = getDateRange(selectedFilter.value);

  const startDate = fromUnixTime(from);
  const endDate = fromUnixTime(to);

  // Normalize to midnight to avoid partial-day splits
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  const dailyTotals = data.reduce((acc, element) => {
    const day = fromUnixTime(element.timestamp);
    day.setHours(0, 0, 0, 0);
    const key = format(day, 'yyyy-MM-dd');
    acc[key] = (acc[key] || 0) + element.value;
    return acc;
  }, {});

  const labels = [];
  const values = [];
  const cursor = new Date(startDate);

  while (cursor <= endDate) {
    const key = format(cursor, 'yyyy-MM-dd');
    labels.push(format(cursor, 'EEE, MMM d'));
    values.push(dailyTotals[key] || 0);
    cursor.setDate(cursor.getDate() + 1);
  }

  return { labels, values };
};

const getDateRange = filter => {
  const now = Math.floor(Date.now() / 1000);
  const nowDate = new Date();

  switch (filter) {
    case '7days':
      return {
        from: now - 7 * 24 * 60 * 60,
        to: now,
      };
    case '14days':
      return {
        from: now - 14 * 24 * 60 * 60,
        to: now,
      };
    case '30days':
      return {
        from: now - 30 * 24 * 60 * 60,
        to: now,
      };
    case 'lastMonth': {
      const lastMonth = subMonths(nowDate, 1);
      const start = startOfMonth(lastMonth);
      const end = endOfMonth(lastMonth);
      return {
        from: Math.floor(start.getTime() / 1000),
        to: Math.floor(end.getTime() / 1000),
      };
    }
    case 'secondLastMonth': {
      const secondLastMonth = subMonths(nowDate, 2);
      const start = startOfMonth(secondLastMonth);
      const end = endOfMonth(secondLastMonth);
      return {
        from: Math.floor(start.getTime() / 1000),
        to: Math.floor(end.getTime() / 1000),
      };
    }
    default:
      return {
        from: now - 7 * 24 * 60 * 60,
        to: now,
      };
  }
};

const collection = computed(() => {
  const { labels, values } = buildDateSeries();

  // Create dataset with proper defaults
  const baseDatasets = chartOptions?.datasets || [{}];
  const datasets = baseDatasets.map(dataset => ({
    ...dataset,
    type: 'line', // Force line chart type
    yAxisID: 'y',
    label: t('OVERVIEW_REPORTS.REPLIES_CHART.HEADER'),
    data: values,
    tension: 0.4, // Smooth line
    borderColor: '#3b82f6', // Blue line color
    backgroundColor: 'rgba(59, 130, 246, 0.1)', // Light blue fill
    borderWidth: 2,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointBackgroundColor: '#3b82f6',
    pointBorderColor: '#fff',
    pointBorderWidth: 2,
    fill: true, // Fill area under line
  }));

  return {
    labels,
    datasets,
  };
});

const fetchRepliesReport = () => {
  const { from, to } = getDateRange(selectedFilter.value);
  const params = {
    metric: 'outgoing_messages_count',
    from,
    to,
    groupBy: 'day',
  };
  if (isAdmin.value) {
    params.type = selectedInbox.value ? 'inbox' : 'account';
    if (selectedInbox.value) {
      params.id = selectedInbox.value.id;
    }
  } else {
    params.type = 'agent';
    params.id = currentUser.value.id;
  }
  store.dispatch('fetchAccountReport', params);
};

const handleInboxAction = ({ value }) => {
  toggleInboxDropdown(false);
  selectedInbox.value = value
    ? safeInboxes.value.find(inbox => inbox.id === value)
    : null;
  fetchRepliesReport();
};

const downloadCSV = () => {
  const { labels, values } = buildDateSeries();

  if (!values.length) {
    alert('No data available to download');
    return;
  }

  // Create CSV content
  let csvContent = 'Date,Outgoing Messages Count\n';
  labels.forEach((label, index) => {
    csvContent += `${label},${values[index]}\n`;
  });

  // Add summary row
  const total = values.reduce((sum, val) => sum + val, 0);
  csvContent += `\nTotal,${total}\n`;

  // Create download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  const filterLabel =
    filterOptions.find(opt => opt.value === selectedFilter.value)?.label ||
    'Report';
  const inboxName = selectedInbox.value
    ? `_${selectedInbox.value.name.replace(/[^a-z0-9]/gi, '_')}`
    : '';
  const fileName = `outgoing_messages${inboxName}_${
    selectedFilter.value
  }_${format(new Date(), 'yyyy-MM-dd')}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

onMounted(() => {
  if (isAdmin.value) {
    store.dispatch('inboxes/get');
  }
  fetchRepliesReport();
});
</script>

<template>
  <MetricCard
    :is-loading="isLoading"
    :loading-message="t('OVERVIEW_REPORTS.REPLIES_CHART.LOADING_MESSAGE')"
  >
    <!-- Custom Header with Filters and Download -->
    <template #header>
      <div class="flex items-center justify-between w-full">
        <!-- Left Side: Title -->
        <div>
          <h3 class="text-lg font-semibold text-gray-900">
            {{ t('OVERVIEW_REPORTS.REPLIES_CHART.HEADER') }}
          </h3>
        </div>

        <!-- Right Side: Filters and Download -->
        <div class="flex items-center gap-2">
          <!-- Date Range Dropdown -->
          <select
            v-model="selectedFilter"
            @change="fetchRepliesReport"
            style="
              appearance: none;
              -webkit-appearance: none;
              -moz-appearance: none;
              background-image: none;
              outline: none;
              padding: 0px;
              margin-bottom: 0px;
              font-size: 13px;
              padding: 5px 10px;
              height: 32px;
              border-radius: 6px;
            "
          >
            <option
              v-for="option in filterOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>

          <!-- Inbox Dropdown -->
          <div
            v-if="isAdmin"
            v-on-clickaway="() => toggleInboxDropdown(false)"
            class="relative flex items-center group"
          >
            <Button
              sm
              slate
              faded
              :label="selectedInboxFilter.label"
              class="rounded-md group-hover:bg-n-alpha-2 max-w-[200px]"
              @click="toggleInboxDropdown()"
            />
            <DropdownMenu
              v-if="showInboxDropdown"
              :menu-items="inboxMenuItems"
              show-search
              :search-placeholder="t('INBOX_REPORTS.SEARCH_INBOX')"
              class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full !min-w-56 max-w-56 max-h-96 overflow-y-auto"
              @action="handleInboxAction($event)"
            />
          </div>

          <!-- Download CSV Button -->
          <button
            @click="downloadCSV"
            :disabled="isLoading"
            class="flex items-center justify-center w-10 h-10 text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Download CSV"
            style="
              background: #8b8d981a;
              border-radius: 4px;
              padding: 10px 10px;
              height: 32px;
              font-size: 12px;
            "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </button>
        </div>
      </div>
    </template>

    <!-- Chart Section -->
    <div class="w-full h-80">
      <!-- Changed from h-72 to h-80 -->
      <LineChart
        v-if="collection.datasets.length && !isLoading"
        :collection="collection"
        :chart-options="chartOptions"
      />
      <span v-else-if="!isLoading" class="text-sm text-n-slate-10">
        {{ $t('REPORT.NO_ENOUGH_DATA') }}
      </span>
    </div>
  </MetricCard>
</template>
